import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LOP30_PROGRAM_SLUG } from '../../utils/programDeepLinks';
import { APP_STORE_URL, PLAY_STORE_URL } from '../../utils/appStoreLinks';
import '../../pages/home/Home.css';

export const createHomeAccountUrl = (opts = {}) => {
  const params = new URLSearchParams();
  if (opts.priceTierId != null) params.set('price_tier_id', String(opts.priceTierId));
  if (opts.productName) params.set('product_name', opts.productName);
  if (opts.tierLabel) params.set('tier_label', opts.tierLabel);
  if (opts.priceAmount != null) params.set('price_amount', String(opts.priceAmount));
  const qs = params.toString();
  return {
    pathname: '/create-account',
    search: qs ? `?${qs}` : ''
  };
};

const NAV_LINKS = [
  { id: 'okter', label: 'Økter' },
  { id: 'programmer', label: 'Programmer' },
  { id: 'gratisokt', label: 'Gratisøkt' },
  { id: 'podcast', label: 'Podcast' },
  { id: 'pris', label: 'Pris' },
  { id: 'om-oss', label: 'Om oss' }
];

const sectionHref = (id, isHome) => (isHome ? `#${id}` : `/#${id}`);

export function HomeHeader() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = location.pathname === '/';
  const trialUrl = createHomeAccountUrl();
  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="home-header">
      <div className="home-container home-header-inner">
        <Link to="/" className="home-logo" onClick={closeMenu}>
          <img src="/lopeglad_white.png" alt="Løpeglad" />
        </Link>

        <nav className="home-nav" aria-label="Hovedmeny">
          {NAV_LINKS.map((link) => (
            <a key={link.id} href={sectionHref(link.id, isHome)}>
              {link.label}
            </a>
          ))}
        </nav>

        <div className="home-header-actions">
          {!loading && (
            <Link
              to={user ? '/dashboard' : { pathname: '/login', search: '' }}
              className="home-login"
            >
              {user ? 'Dashboard' : 'Logg inn'}
            </Link>
          )}
          <Link to={trialUrl} className="home-btn home-btn--primary home-btn--sm">
            Start gratis i 7 dager
          </Link>
          <button
            type="button"
            className={`home-burger${menuOpen ? ' is-open' : ''}`}
            aria-label={menuOpen ? 'Lukk meny' : 'Åpne meny'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      <div
        className={`home-mobile-backdrop${menuOpen ? ' is-open' : ''}`}
        onClick={closeMenu}
        aria-hidden={!menuOpen}
      />
      <div className={`home-mobile-menu${menuOpen ? ' is-open' : ''}`} aria-hidden={!menuOpen}>
        {NAV_LINKS.map((link) => (
          <a
            key={link.id}
            href={sectionHref(link.id, isHome)}
            onClick={closeMenu}
          >
            {link.label}
          </a>
        ))}
        {!loading && (
          <Link
            to={user ? '/dashboard' : { pathname: '/login', search: '' }}
            onClick={closeMenu}
          >
            {user ? 'Dashboard' : 'Logg inn'}
          </Link>
        )}
        <Link to={trialUrl} className="home-btn home-btn--primary" onClick={closeMenu}>
          Start gratis i 7 dager
        </Link>
      </div>
    </header>
  );
}

export function HomeFooter() {
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <footer className="home-footer">
      <div className="home-container home-footer-grid">
        <div className="home-footer-brand">
          <img src="/lopeglad_white.png" alt="Løpeglad" />
          <div className="home-social">
            <a href="https://www.instagram.com/lopeglad.no/" aria-label="Instagram">
              <i className="fab fa-instagram" />
            </a>
            <a href="https://www.facebook.com/lopeglad.no" aria-label="Facebook">
              <i className="fab fa-facebook" />
            </a>
            <a href="https://www.youtube.com/@lopeglad" aria-label="YouTube">
              <i className="fab fa-youtube" />
            </a>
          </div>
        </div>

        <div className="home-footer-cols">
          <div>
            <h4>Appen</h4>
            <a href={sectionHref('appen', isHome)}>Slik fungerer det</a>
            <a href={sectionHref('okter', isHome)}>Økter</a>
            <a href={sectionHref('gratisokt', isHome)}>Gratisøkt</a>
          </div>
          <div>
            <h4>Programmer</h4>
            <Link to={`/program/${LOP30_PROGRAM_SLUG}`}>Løp 30 om 12 uker</Link>
            <a href={sectionHref('programmer', isHome)}>Alle programmer</a>
            <a href={sectionHref('pris', isHome)}>Pris</a>
          </div>
          <div>
            <h4>Om oss</h4>
            <a href={sectionHref('om-oss', isHome)}>Elin &amp; Lars-Erik</a>
            <a href="mailto:post@lopeglad.no">Kontakt oss</a>
            <Link to="/vilkår">Vilkår</Link>
            <Link to="/personvern">Personvern</Link>
          </div>
        </div>

        <div className="home-footer-stores">
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img src="/img/AppStore.png" alt="App Store" />
          </a>
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img src="/img/GooglePlayNorwegian.png" alt="Google Play" />
          </a>
        </div>
      </div>
      <div className="home-container home-footer-copy">
        <p>© {new Date().getFullYear()} Løpeglad</p>
      </div>
    </footer>
  );
}

export default function HomeMarketingLayout({ children }) {
  return (
    <div className="home-page">
      <HomeHeader />
      {children}
      <HomeFooter />
    </div>
  );
}
